import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import TimePickerCustom from 'src/components/common/field/TimePickerCustom';
import StackRow from './StackRow';
import RadioField from './RadioField';

const inputStyle = {
  fontSize: '14px',
  my: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#BDBDBD',
  },
  '& .MuiInputBase-input': { padding: '8px 14px', height: '32px' },
  '& input::placeholder': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#CCD1D9',
    opacity: 1,
  },
  backgroundColor: 'primary.light',
  flex: 1,
};

const dateStyle = {
  my: '8px',
  flex: 1,
  '& .MuiInputBase-input.MuiOutlinedInput-input': {
    py: '8px',
    fontSize: '14px',
    height: '32px',
  },
};

const options = [
  { label: 'テキスト入力', value: 'text' },
  // { label: '数値入力', value: 'number' },
  { label: '長文入力', value: 'textarea' },
  { label: '日付選択', value: 'date' },
  { label: '時間選択', value: 'time' },
  { label: 'チェックボックス（複数選択が可能）', value: 'checkbox' },
  { label: 'ラジオボタン（1つだけ選択可能）', value: 'radio' },
  { label: 'セレクトメニュー（プルダウンから選択）', value: 'select' },
];

const GenerateCreateField = (props: any) => {
  const field = props.field;

  const handleInputChange = (event: any, position: number) => {
    const { name, value } = event.target;
    let question = { ...field, [name]: value };
    props.handleChangeValue(question, position);
    props.handleModified(field.order);
  };

  const handleInputOptionChange = (
    event: any,
    idx: number,
    position: number
  ) => {
    const { value } = event.target;
    let question = { ...field };
    question['options'][idx] = value;
    props.handleChangeValue(question, position);
    props.handleModified(field.order);
  };

  const handleInputChildQuestions = (value: any, position: number) => {
    let question = { ...field };

    const fields = Array.isArray(value) ? value.reduce(
      (temp: any, current: any, index: number) => {
        current['order'] = index + 1;
        current['field_id'] = `q${props.position + 1}_${current.parent_id}_${index + 1}`;
        temp.push(current);
        return temp;
      },
      []
    ) : [];

    question['children_fields'] = fields;
    props.handleChangeValue(question, position);
    props.handleModified(field.order);
  }

  const handleSwitchChange = (event: any, position: number) => {
    const { name, checked } = event.target;
    let question = { ...field, [name]: checked };
    props.handleChangeValue(question, position);
    props.handleModified(field.order);
  };

  const handleSelectChange = (event: any, position: number) => {
    const { value } = event.target;
    let defaultValue = {
      type: value,
      label: '',
      help_text: '',
      field_id: '',
      required: false,
      order: field.order || '',
    };
    let question = {};
    switch (value) {
      case 'text':
        question = { ...defaultValue, min_length: '', max_length: '' };
        break;
      case 'number':
        question = { ...defaultValue, min: '', max: '' };
        break;
      case 'textarea':
        question = { ...defaultValue, min_length: '', max_length: '', rows: 4 };
        break;
      case 'date':
        question = { ...defaultValue, min_date: null, max_date: null };
        break;
      case 'time':
        question = { ...defaultValue, min_time: '', max_time: '' };
        break;
      case 'checkbox':
        question = {
          ...defaultValue,
          min_selection: '',
          max_selection: '',
          options: [''],
          others: '',
        };
        break;
      case 'radio':
        question = { ...defaultValue, options: [''], others: '' };
        break;
      case 'select':
        question = {
          ...defaultValue,
          options: [''],
          others: '',
          allow_multiple: false,
        };
        break;
      default:
        question = {
          ...defaultValue,
          min_length: '',
          max_length: '',
        };
        break;
    }
    props.handleChangeValue(question, position);
    props.handleModified(field.order);
  };

  const addOption = (position: number) => {
    let question = { ...field };
    question['options'].push('');
    props.handleChangeValue(question, position);
    props.handleModified(field.order);
  };

  const removeOption = (idx: number, position: number) => {
    let question = { ...field };
    question['options'].splice(idx, 1);
    props.handleChangeValue(question, position);
    props.handleModified(field.order);
  };

  const handleDateChange = (
    newValue: dayjs.Dayjs | null,
    name: string,
    position: number
  ) => {
    let question = { ...field };
    question[name] = newValue?.format('YYYY/MM/DD')
      ? newValue?.format('YYYY/MM/DD')
      : null;
    props.handleChangeValue(question, position);
    props.handleModified(field.order);
  };

  const handleChangeTime =
    (name: string, position: number) => (date: Dayjs | null) => {
      const timeFormat = date ? date.format('HH:mm') : null;
      let question = { ...field };
      question[name] = timeFormat;
      props.handleChangeValue(question, position);
      props.handleModified(field.order);
    };

  return (
    <Stack width={'100%'}>
      <StackRow>
        <Typography width={'120px'}>タイプ</Typography>
        <Select
          value={field.type}
          onChange={(e) => handleSelectChange(e, props.position)}
          sx={{
            backgroundColor: 'primary.light',
            height: '48px',
            '& .MuiInputBase-input.MuiOutlinedInput-input': {
              p: '6px 14px',
            },
            flex: 1,
          }}
        >
          {options.map((option: any, idx: number) => {
            return (
              <MenuItem value={option.value} key={idx}>
                {option.label}
              </MenuItem>
            );
          })}
        </Select>
      </StackRow>
      <StackRow>
        <Typography width={'120px'}>質問</Typography>
        <OutlinedInput
          sx={inputStyle}
          inputProps={{
            sx: {
              backgroundColor: 'primary.light',
            },
          }}
          name={'label'}
          value={field.label}
          onChange={(event) => handleInputChange(event, props.position)}
        />
      </StackRow>
      <StackRow>
        <Typography width={'120px'}>補足説明</Typography>
        <OutlinedInput
          sx={inputStyle}
          inputProps={{
            sx: {
              backgroundColor: 'primary.light',
            },
          }}
          name={'help_text'}
          value={field.help_text}
          onChange={(event) => handleInputChange(event, props.position)}
        />
      </StackRow>
      <StackRow>
        <Typography width={'120px'}>必須</Typography>
        <Switch
          size='medium'
          sx={{ mr: '7px' }}
          checked={Boolean(field.required)}
          name='required'
          onChange={(event) => handleSwitchChange(event, props.position)}
        />
      </StackRow>
      {(field.type === 'text' || field.type === 'textarea') && (
        <>
          <StackRow>
            <Typography width={'120px'}>最小文字数</Typography>
            <OutlinedInput
              sx={inputStyle}
              type='number'
              inputProps={{
                sx: {
                  backgroundColor: 'primary.light',
                },
              }}
              name={'min_length'}
              value={field.min_length}
              onChange={(event) => handleInputChange(event, props.position)}
            />
          </StackRow>
          <StackRow>
            <Typography width={'120px'}>最大文字数</Typography>
            <OutlinedInput
              sx={inputStyle}
              type='number'
              inputProps={{
                sx: {
                  backgroundColor: 'primary.light',
                },
              }}
              name={'max_length'}
              value={field.max_length}
              onChange={(event) => handleInputChange(event, props.position)}
            />
          </StackRow>
        </>
      )}
      {field.type === 'textarea' && (
        <StackRow>
          <Typography width={'120px'}>行</Typography>
          <OutlinedInput
            sx={inputStyle}
            type='number'
            inputProps={{
              sx: {
                backgroundColor: 'primary.light',
              },
            }}
            name={'rows'}
            value={field.rows}
            onChange={(event) => handleInputChange(event, props.position)}
          />
        </StackRow>
      )}
      {field.type === 'number' && (
        <>
          <StackRow>
            <Typography width={'120px'}>最小</Typography>
            <OutlinedInput
              sx={inputStyle}
              type='number'
              inputProps={{
                sx: {
                  backgroundColor: 'primary.light',
                },
              }}
              name={'min'}
              value={field.min}
              onChange={(event) => handleInputChange(event, props.position)}
            />
          </StackRow>
          <StackRow>
            <Typography width={'120px'}>最大</Typography>
            <OutlinedInput
              sx={inputStyle}
              type='number'
              inputProps={{
                sx: {
                  backgroundColor: 'primary.light',
                },
              }}
              name={'max'}
              value={field.max}
              onChange={(event) => handleInputChange(event, props.position)}
            />
          </StackRow>
        </>
      )}
      {field.type === 'date' && (
        <>
          <StackRow>
            <Typography width={'120px'}>最小日付</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name='min_date'
                value={field.min_date ? dayjs(field.min_date) : null}
                onChange={(newValue) =>
                  handleDateChange(newValue, 'min_date', props.position)
                }
                format='YYYY/MM/DD'
                sx={dateStyle}
              />
            </LocalizationProvider>
          </StackRow>
          <StackRow>
            <Typography width={'120px'}>最大日付</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name='max_date'
                value={field.max_date ? dayjs(field.max_date) : null}
                onChange={(newValue) =>
                  handleDateChange(newValue, 'max_date', props.position)
                }
                format='YYYY/MM/DD'
                sx={dateStyle}
              />
            </LocalizationProvider>
          </StackRow>
        </>
      )}
      {field.type === 'time' && (
        <>
          <StackRow>
            <Typography width={'120px'}>最小時間</Typography>
            <TimePickerCustom
              value={dayjs(dayjs().format('YYYY-MM-DD') + ' ' + field.min_time)}
              onChange={handleChangeTime('min_time', props.position)}
            />
          </StackRow>
          <StackRow>
            <Typography width={'120px'}>最大時間</Typography>
            <TimePickerCustom
              value={dayjs(dayjs().format('YYYY-MM-DD') + ' ' + field.max_time)}
              onChange={handleChangeTime('max_time', props.position)}
            />
          </StackRow>
        </>
      )}
      {field.type === 'checkbox' && (
        <>
          <StackRow>
            <Typography width={'120px'}>最小選択</Typography>
            <OutlinedInput
              sx={inputStyle}
              type='number'
              inputProps={{
                sx: {
                  backgroundColor: 'primary.light',
                },
              }}
              name={'min_selection'}
              value={field.min_selection}
              onChange={(event) => handleInputChange(event, props.position)}
            />
          </StackRow>
          <StackRow>
            <Typography width={'120px'}>最大選択</Typography>
            <OutlinedInput
              sx={inputStyle}
              type='number'
              inputProps={{
                sx: {
                  backgroundColor: 'primary.light',
                },
              }}
              name={'max_selection'}
              value={field.max_selection}
              onChange={(event) => handleInputChange(event, props.position)}
            />
          </StackRow>
        </>
      )}
      {(field.type === 'checkbox' || field.type === 'select') && (
        <>
          <Stack
            direction='row'
            spacing={'8px'}
            justifyContent={'flex-start'}
            // alignItems={'center'}
            width={'100%'}
            my={'8px'}
          >
            <Typography width={'120px'} style={{ marginTop: '8px' }}>
              選択肢
            </Typography>
            <Stack>
              {field.options.map((option: string, idx: number) => {
                return (
                  <Stack direction={'row'} spacing={'4px'} mb={'4px'} key={idx}>
                    <OutlinedInput
                      sx={inputStyle}
                      inputProps={{
                        sx: {
                          backgroundColor: 'primary.light',
                        },
                      }}
                      name={'option'}
                      value={option}
                      onChange={(event) =>
                        handleInputOptionChange(event, idx, props.position)
                      }
                    />
                    <Box
                      component={'img'}
                      src='/images/icons/ic-trash.svg'
                      alt='trash'
                      sx={{ cursor: 'pointer' }}
                      onClick={() => removeOption(idx, props.position)}
                    />
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
          <StackRow>
            <Typography width={'120px'}></Typography>
            <Stack>
              <BtnOutlineGradient
                text='選択肢を追加'
                handleButtonClick={() => addOption(props.position)}
                width='100px'
              />
            </Stack>
          </StackRow>
        </>
      )}
      {field.type === 'select' && (
        <>
          <StackRow>
            <Typography width={'120px'}>複数回答を可能にする</Typography>
            <Switch
              size='medium'
              sx={{ mr: '7px' }}
              checked={Boolean(field.allow_multiple)}
              name='allow_multiple'
              onChange={(event) => handleSwitchChange(event, props.position)}
            />
          </StackRow>
        </>
      )}

      {field.type === 'radio' && (
        <>
          <Stack
            direction='row'
            spacing={'8px'}
            justifyContent={'flex-start'}
            // alignItems={'center'}
            width={'100%'}
            my={'8px'}
          >
            <Typography width={'120px'} style={{ marginTop: '8px' }}>
              選択肢
            </Typography>
            <Stack flex={1}>
              {field.options.map((option: string, idx: number) => {
                return (
                  <RadioField
                    key={idx}
                    idx={idx}
                    option={option}
                    children_fields={field.children_fields}
                    position={props.position}
                    sx={inputStyle}
                    handleInputOptionChange={handleInputOptionChange}
                    removeOption={removeOption}
                    handleInputChildQuestions={handleInputChildQuestions}
                  />
                );
              })}
            </Stack>
          </Stack>
          <StackRow>
            <Typography width={'120px'}></Typography>
            <Stack>
              <BtnOutlineGradient
                text='選択肢を追加'
                handleButtonClick={() => addOption(props.position)}
                width='100px'
              />
            </Stack>
          </StackRow>
        </>
      )}
      <Stack justifyContent={'center'} alignItems={'end'}>
        <BtnOutlineGradient
          text='削除'
          handleButtonClick={() => props.removeQuestion(props.position)}
        />
      </Stack>
      <Divider sx={{ my: '8px' }} />
    </Stack>
  );
};

export default GenerateCreateField;
